import type { Chat, Settings, ChatGroup, SidebarItem, ChatSummary, ChatMeta, ChatContent, StorageSnapshot, BinaryObject, Volume, VolumeType } from '@/01-models/types';
import type { ChatMetaDto, ChatGroupDto, HierarchyDto } from '@/00-storage/00-dto/dto';
import type { BinaryObjectId, ChatGroupId, ChatId, VolumeId } from '@/01-models/ids';
import { idToRaw } from '@/01-models/ids';
import type { ExperimentalNaidanRpcRegistryDto } from '@/00-storage/00-dto/experimental-naidan-rpc.dto';
import type { StoredVolumeAccess } from '@/01-models/volume-access';

export type { ChatSummary };

/**
 * Base Storage Provider
 * Provides common logic for transforming DTOs to Domain models.
 */
export abstract class IStorageProvider {
  abstract init(): Promise<void>;

  /**
   * Whether this provider supports efficient binary persistence (e.g. OPFS).
   * LocalStorage returns false to indicate potential capacity issues.
   */
  abstract readonly canPersistBinary: boolean;

  // An absent registry is distinct from unreadable or invalid stored data.
  abstract loadNaidanRpcRegistry(): Promise<ExperimentalNaidanRpcRegistryDto | undefined>;
  abstract saveNaidanRpcRegistry({ registry }: { registry: ExperimentalNaidanRpcRegistryDto | undefined }): Promise<void>;

  // --- Volume Management ---

  abstract listVolumes(): AsyncIterable<Volume>;

  abstract createVolume({ name, type, sourceHandle }: {
    name: string,
    type: VolumeType,
    sourceHandle: FileSystemDirectoryHandle,
  }): Promise<Volume>;

  abstract createVolumeFromFiles({ name, entries, onProgress, signal }: {
    name: string,
    entries: Array<{ file: File, relativePath: string }>,
    onProgress?: ({ processed, total }: { processed: number, total: number }) => void,
    signal?: AbortSignal,
  }): Promise<Volume>;

  abstract getVolumeDirectoryHandle({ volumeId }: {
    volumeId: VolumeId,
  }): Promise<FileSystemDirectoryHandle | null>;

  abstract deleteVolume({ volumeId }: {
    volumeId: VolumeId,
  }): Promise<void>;

  abstract renameVolume({ volumeId, name }: {
    volumeId: VolumeId,
    name: string,
  }): Promise<void>;

  async ensureVolumeDirectory({ volumeId: _volumeId }: { volumeId: VolumeId }): Promise<FileSystemDirectoryHandle> {
    throw new Error('This storage provider cannot create a browser folder');
  }

  async publishVolume({ volume: _volume, root: _root }: { volume: Volume, root: FileSystemDirectoryHandle | undefined }): Promise<void> {
    throw new Error('This storage provider cannot publish a browser folder');
  }

  async deleteUnpublishedVolumeDirectory({ volumeId: _volumeId }: { volumeId: VolumeId }): Promise<void> {
    throw new Error('This storage provider cannot remove an unpublished browser folder');
  }

  async probeVolumeAccess({ volumeId }: { volumeId: VolumeId }): Promise<StoredVolumeAccess> {
    for await (const volume of this.listVolumes()) {
      if (idToRaw({ id: volume.id }) !== idToRaw({ id: volumeId })) {
        continue;
      }
      const handle = await this.getVolumeDirectoryHandle({ volumeId });
      if (handle !== null) {
        return { kind: 'native', volume, handle };
      }
      break;
    }
    return { kind: 'missing', volumeId };
  }

  // --- Data Access Methods ---
  protected abstract listChatMetasRaw(): Promise<ChatMetaDto[]>;
  protected abstract listChatGroupsRaw(): Promise<ChatGroupDto[]>;

  // --- Hierarchy Management ---
  abstract loadHierarchy(): Promise<HierarchyDto | null>;
  abstract saveHierarchy({ hierarchy }: { hierarchy: HierarchyDto }): Promise<void>;

  // --- Bulk Operations (Migration) ---
  abstract dump(): Promise<StorageSnapshot>;
  abstract restore({ snapshot }: { snapshot: StorageSnapshot }): Promise<void>;

  // --- Public Domain API (Default Implementations) ---

  /**
   * Checks every persisted mount owner, including records that are not currently
   * reachable from the hierarchy. The scan short-circuits before chat metadata
   * whenever settings or a chat group already references the volume.
   */
  public async hasVolumeMountReference({ volumeId }: { volumeId: VolumeId }): Promise<boolean> {
    const settings = await this.loadSettings();
    if (settings?.mounts.some(mount => mount.volumeId === volumeId)) {
      return true;
    }

    const rawVolumeId = idToRaw({ id: volumeId });
    const chatGroups = await this.listChatGroupsRaw();
    if (chatGroups.some(group => group.mounts?.some(mount => mount.volumeId === rawVolumeId))) {
      return true;
    }

    const chatMetas = await this.listChatMetasRaw();
    return chatMetas.some(meta => meta.mounts?.some(mount => mount.volumeId === rawVolumeId));
  }

  /**
   * Returns sorted ChatGroups with their nested items.
   */
  public async listChatGroups(): Promise<ChatGroup[]> {
    const sidebar = await this.getSidebarStructure();
    return sidebar
      .filter((item): item is Extract<SidebarItem, { type: 'chat_group' }> => item.type === 'chat_group')
      .map(item => item.chatGroup);
  }

  /**
   * Returns a flat list of all ChatSummaries, ordered by their sidebar position.
   */
  public async listChats(): Promise<ChatSummary[]> {
    const sidebar = await this.getSidebarStructure();
    const allSummaries: ChatSummary[] = [];

    sidebar.forEach(item => {
      switch (item.type) {
      case 'chat_group':
        item.chatGroup.items.forEach(nested => {
          allSummaries.push(nested.chat);
        });
        break;
      case 'chat':
        allSummaries.push(item.chat);
        break;
      default: {
        const _ex: never = item;
        throw new Error(`Unhandled sidebar item type: ${_ex}`);
      }
      }
    });
    return allSummaries;
  }

  /**
   * Centralized method to get the full sorted hierarchy using mappers.
   */
  public abstract getSidebarStructure(): Promise<SidebarItem[]>;

  // --- Persistence Methods ---

  /**
   * Persists chat metadata (title, updated date, etc).
   */
  abstract saveChatMeta({ meta }: { meta: ChatMeta }): Promise<void>;

  /**
   * Saves only the chat content (message tree) to a dedicated file.
   */
  abstract saveChatContent({ id, content }: { id: ChatId, content: ChatContent }): Promise<void>;

  // Missing records return null; malformed or unreadable records reject.
  // Callers must not treat a read failure as permission to create new content.
  abstract loadChat({ id }: { id: ChatId }): Promise<Chat | null>;
  abstract loadChatMeta({ id }: { id: ChatId }): Promise<ChatMeta | null>;
  abstract loadChatContent({ id }: { id: ChatId }): Promise<ChatContent | null>;
  /**
   * Loads chat content without hydrating attachment-backed data.
   * Attachment descriptors may remain on the returned messages.
   * Missing content returns null; parsing and I/O errors reject.
   */
  abstract loadChatContentWithoutAttachments({ id }: { id: ChatId }): Promise<ChatContent | null>;
  abstract deleteChat({ id }: { id: ChatId }): Promise<void>;

  abstract saveChatGroup({ chatGroup }: { chatGroup: ChatGroup }): Promise<void>;
  abstract loadChatGroupConfiguration({ id }: { id: ChatGroupId }): Promise<Omit<ChatGroup, 'items'> | null>;
  abstract loadChatGroup({ id }: { id: ChatGroupId }): Promise<ChatGroup | null>;
  abstract deleteChatGroup({ id }: { id: ChatGroupId }): Promise<void>;

  abstract saveSettings({ settings }: { settings: Settings }): Promise<void>;
  abstract loadSettings(): Promise<Settings | null>;
  abstract clearAll(): Promise<void>;

  // --- File Storage ---
  abstract saveFile({ blob, binaryObjectId, name, mimeType }: {
    blob: Blob,
    binaryObjectId: BinaryObjectId,
    name: string,
    mimeType?: string,
  }): Promise<void>;
  abstract getFile({ binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<Blob | null>;
  abstract getBinaryObject({ binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<BinaryObject | null>;
  abstract hasAttachments(): Promise<boolean>;
  abstract listBinaryObjects(): AsyncIterable<BinaryObject>;
  abstract deleteBinaryObject({ binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<void>;
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
